// TODO(PE-1215 follow-up): split this shared component into
// `past-visit-item/kinsa.tsx` and `past-visit-item/owlet.tsx` — two
// separate implementations, each hardcoding its own brand's behavior —
// and pick between them via `resolveAlias` (see apps/platform/next.config.ts,
// per the "Brand-specific Code" recipe in CLAUDE.md). The current
// config-prop pattern was a stepping stone to delete the in-file brand
// ternaries; the next move is to delete the prop seam entirely.
import RedXIcon from "@repo/ui/resources/red-x-icon.svg";
import Image from "next/image";
import Link from "next/link";
import React, { type ReactNode } from "react";
import { AvatarGroup } from "@repo/ui/avatar";
import {
  BodyText,
  BodyTitleText,
  SublineCondensedText,
} from "src/components/KinsaTypography";
import { twMerge } from "tailwind-merge";
import type { PastVisitDisplay } from "./past-visit-display";

// Default (Kinsa) chevron rendered on the right edge of a clickable card.
const kinsaChevron: ReactNode = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="select-none"
    onContextMenu={(e) => e.preventDefault()}
  >
    <path
      d="M9.53027 5.47012C9.82324 5.76309 9.82324 6.23887 9.53027 6.53184L5.03027 11.0318C4.7373 11.3248 4.26152 11.3248 3.96855 11.0318C3.67559 10.7389 3.67559 10.2631 3.96855 9.97012L7.93887 5.9998L3.9709 2.02949C3.67793 1.73652 3.67793 1.26074 3.9709 0.967773C4.26387 0.674805 4.73965 0.674805 5.03262 0.967773L9.53262 5.46777L9.53027 5.47012Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Brand-specific visual config for past-visit cards.
 *
 * This is the SUBSET of fields that `past-visit-item` reads. The richer
 * `VisitCardBrandConfig` in `visit-list-item.tsx` extends this, so a single
 * `VisitCardBrandConfig` per brand satisfies both components — callers thread
 * one config down the chain.
 *
 * Defaults match Kinsa. Owlet supplies overrides in
 * `owlet/visit-list-config.tsx` (`owletVisitCardConfig`).
 */
export interface PastVisitCardConfig {
  /** Card-wrapper classes for past (completed/cancelled) visits */
  pastCardClasses: string;
  /** Avatar layout for horizontal (completed/cancelled) cards */
  avatarLayout: "diagonal" | "overlap";
  /** Dim avatar for cancelled visits */
  dimCancelledAvatars: boolean;
  /** Typography component for the date line on completed cards. */
  dateTextComponent: typeof BodyText | typeof SublineCondensedText;
  /** Text class for cancelled subtitle */
  cancelledTextClass: string;
  /** Cancelled visit subtitle: "inline-date" = "Canceled on {date}", "label-only" = "Canceled Visit" */
  cancelledDateFormat: "inline-date" | "label-only";
  /** Chevron SVG node for horizontal (completed/cancelled) cards */
  horizontalChevron: ReactNode;
}

export const defaultPastVisitConfig: PastVisitCardConfig = {
  pastCardClasses: "p-6 min-h-[94px] bg-surface-elevated",
  avatarLayout: "overlap",
  dimCancelledAvatars: false,
  dateTextComponent: SublineCondensedText,
  cancelledTextClass: "text-foreground/70",
  cancelledDateFormat: "label-only",
  horizontalChevron: kinsaChevron,
};

interface PastVisitItemProps extends PastVisitDisplay {
  className?: string;
  brandConfig?: PastVisitCardConfig;
}

/**
 * Dumb presenter for a single past visit row.
 *
 * Accepts a resolved view-model only — no Case parsing, no avatar lookup,
 * no network calls. Use `derivePastVisitDisplay` from `past-visit-display.ts`
 * to compute props from a `Case`.
 */
const PastVisitItem: React.FC<PastVisitItemProps> = ({
  title,
  dateText,
  firstAvatar,
  firstAvatarInitial,
  secondAvatar,
  secondAvatarUrl,
  href,
  cancelled,
  className,
  brandConfig = defaultPastVisitConfig,
}) => {
  const clickable = !!href;
  const DateTextComponent = brandConfig.dateTextComponent;

  const card = (
    <div
      className={twMerge(
        "relative flex items-center gap-3 border-[0.5px] border-black/8 w-full",
        "rounded-2xl transition-all duration-200 select-none",
        brandConfig.pastCardClasses,
        "shadow-[0px_2px_10px_0px_rgba(5,57,68,0.05)]",
        clickable && "cursor-pointer",
        className
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div
        className={`flex-shrink-0 ${
          brandConfig.dimCancelledAvatars && cancelled ? "opacity-50" : ""
        }`}
      >
        <AvatarGroup
          firstAvatar={firstAvatar}
          firstAvatarInitial={firstAvatarInitial}
          secondAvatar={secondAvatar}
          secondAvatarUrl={secondAvatarUrl}
          size="small"
          state="assigned"
          layout={brandConfig.avatarLayout}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <BodyTitleText
          as="p"
          className="m-0 font-sans font-bold text-foreground truncate"
        >
          {title}
        </BodyTitleText>
        {cancelled ? (
          <BodyText as="p" className={`m-0 ${brandConfig.cancelledTextClass}`}>
            <span className="inline-flex items-center gap-1">
              <Image
                alt="Canceled"
                src={RedXIcon}
                width={12}
                height={12}
                className="select-none"
                draggable={false}
                unoptimized
              />
              {brandConfig.cancelledDateFormat === "inline-date" && dateText
                ? `Canceled on ${dateText}`
                : "Canceled Visit"}
            </span>
          </BodyText>
        ) : (
          <DateTextComponent as="p" className="m-0 text-foreground">
            {dateText}
          </DateTextComponent>
        )}
      </div>

      {clickable && (
        <div className="flex items-center justify-center flex-shrink-0 text-foreground">
          {brandConfig.horizontalChevron}
        </div>
      )}
    </div>
  );

  if (clickable && href) {
    return (
      <Link title="visit-card" href={href} className="block w-full">
        {card}
      </Link>
    );
  }
  return card;
};

export default PastVisitItem;
